import React from "react";
import { Link, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  Menu,
  User,
  LogOut,
  Settings,
  LayoutDashboard,
  TrendingUp,
  X,
  Heart,
} from "lucide-react";
import { useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { cn } from "@/lib/utils";

const Navbar = () => {
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { user, signOut } = useAuth();

  const isActive = (path: string) => {
    if (path === "/") return location.pathname === "/";
    return location.pathname === path || location.pathname.startsWith(path + "/");
  };

  const handleSignOut = async () => {
    await signOut();
  };

  const getUserInitials = () => {
    if (!user?.email) return "U";
    return user.email.charAt(0).toUpperCase();
  };

  const navItems = [
    { path: "/startups", label: "Ideas" },
    { path: "/create", label: "Create" },
    { path: "/pricing", label: "Pricing" },
  ];

  return (
    <nav
      className="sticky top-0 z-50 w-full"
      style={{
        background: "rgba(237, 233, 248, 0.88)",
        backdropFilter: "blur(24px)",
        WebkitBackdropFilter: "blur(24px)",
        borderBottom: "1px solid rgba(255, 255, 255, 0.55)",
      }}
    >
      <div className="max-w-7xl mx-auto px-6 flex h-16 items-center justify-between">
        <Link to="/" className="flex items-center gap-2 group">
          <span
            className="text-[22px] text-[#18151f]"
            style={{ fontFamily: "'Instrument Serif', serif" }}
          >
            DayOne
          </span>
        </Link>

        {/* Desktop Navigation */}
        <div className="hidden md:flex items-center gap-1">
          {navItems.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              className={cn(
                "text-[13px] px-4 py-2 rounded-full transition-colors",
                isActive(item.path)
                  ? "text-[#7c6af0] bg-[rgba(124,106,240,0.12)] font-medium"
                  : "text-[#6b5f8a] hover:text-[#18151f]"
              )}
              style={{ fontFamily: "'DM Sans', sans-serif" }}
            >
              {item.label}
            </Link>
          ))}
          <Link
            to="/create?demo=1"
            className="text-[13px] px-4 py-2 rounded-full text-[#7c6af0] hover:bg-[rgba(124,106,240,0.08)] transition-colors ml-1"
            style={{ fontFamily: "'DM Sans', sans-serif" }}
          >
            Try Demo
          </Link>
        </div>

        <div className="hidden md:flex items-center gap-3">
          {user ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className="rounded-full">
                  <Avatar className="h-9 w-9 border-2 border-[rgba(124,106,240,0.2)]">
                    <AvatarImage
                      src={user.user_metadata?.avatar_url}
                      alt={user.email || ""}
                    />
                    <AvatarFallback className="bg-[rgba(124,106,240,0.1)] text-[#7c6af0] font-medium text-sm">
                      {getUserInitials()}
                    </AvatarFallback>
                  </Avatar>
                </Button>
              </DropdownMenuTrigger>

              <DropdownMenuContent
                className="w-56 rounded-xl bg-white/90 backdrop-blur-xl border border-white/80"
                align="end"
                forceMount
              >
                <DropdownMenuLabel className="font-normal">
                  <div className="flex flex-col space-y-1">
                    <p className="text-sm font-medium leading-none text-[#18151f]">
                      {user.user_metadata?.full_name ||
                        user.user_metadata?.name ||
                        "User"}
                    </p>
                    <p className="text-xs leading-none text-[#6b5f8a]">
                      {user.email}
                    </p>
                  </div>
                </DropdownMenuLabel>

                <DropdownMenuSeparator className="bg-[rgba(124,106,240,0.1)]" />

                <DropdownMenuItem asChild>
                  <Link to="/saved" className="cursor-pointer text-[#18151f]">
                    <Heart className="mr-2 h-4 w-4 text-[#7c6af0]" />
                    Saved Ideas
                  </Link>
                </DropdownMenuItem>

                <DropdownMenuItem asChild>
                  <Link to="/dashboard" className="cursor-pointer text-[#18151f]">
                    <LayoutDashboard className="mr-2 h-4 w-4 text-[#7c6af0]" />
                    Dashboard
                  </Link>
                </DropdownMenuItem>

                <DropdownMenuItem asChild>
                  <Link to="/trends" className="cursor-pointer text-[#18151f]">
                    <TrendingUp className="mr-2 h-4 w-4 text-[#7c6af0]" />
                    Trends
                  </Link>
                </DropdownMenuItem>

                <DropdownMenuItem asChild>
                  <Link to="/profile" className="cursor-pointer text-[#18151f]">
                    <User className="mr-2 h-4 w-4 text-[#7c6af0]" />
                    Profile
                  </Link>
                </DropdownMenuItem>

                <DropdownMenuItem asChild>
                  <Link to="/settings" className="cursor-pointer text-[#18151f]">
                    <Settings className="mr-2 h-4 w-4 text-[#7c6af0]" />
                    Settings
                  </Link>
                </DropdownMenuItem>

                <DropdownMenuSeparator className="bg-[rgba(124,106,240,0.1)]" />

                <DropdownMenuItem
                  onClick={handleSignOut}
                  className="cursor-pointer text-red-600"
                >
                  <LogOut className="mr-2 h-4 w-4" />
                  Sign out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <>
              <Link
                to="/signin"
                className="text-[13px] text-[#6b5f8a] hover:text-[#18151f] transition-colors"
                style={{ fontFamily: "'DM Sans', sans-serif" }}
              >
                Sign in
              </Link>

              <Link
                to="/create"
                className="text-[13px] font-medium text-white px-5 py-2.5 rounded-full transition-colors"
                style={{
                  fontFamily: "'DM Sans', sans-serif",
                  background: "#18151f",
                }}
              >
                Start Creating
              </Link>
            </>
          )}
        </div>

        {/* Mobile Menu Button */}
        <Button
          variant="ghost"
          size="icon"
          className="md:hidden text-[#18151f]"
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          aria-label="Toggle menu"
        >
          {mobileMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </Button>
      </div>

      {/* Mobile Navigation */}
      {mobileMenuOpen && (
        <div
          className="md:hidden p-4"
          style={{
            background: "rgba(237, 233, 248, 0.98)",
            backdropFilter: "blur(24px)",
            borderTop: "1px solid rgba(255, 255, 255, 0.55)",
          }}
        >
          <div className="flex flex-col gap-2">
            {navItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                onClick={() => setMobileMenuOpen(false)}
                className={cn(
                  "px-4 py-3 text-base font-medium rounded-xl transition-colors",
                  isActive(item.path)
                    ? "bg-[rgba(124,106,240,0.12)] text-[#7c6af0]"
                    : "text-[#6b5f8a] hover:text-[#18151f] hover:bg-white/50"
                )}
                style={{ fontFamily: "'DM Sans', sans-serif" }}
              >
                {item.label}
              </Link>
            ))}
            <Link
              to="/create?demo=1"
              onClick={() => setMobileMenuOpen(false)}
              className="px-4 py-3 text-base font-medium rounded-xl text-[#7c6af0] hover:bg-[rgba(124,106,240,0.08)] transition-colors"
              style={{ fontFamily: "'DM Sans', sans-serif" }}
            >
              Try Demo
            </Link>

            <div className="pt-4 mt-2 border-t border-white/50">
              {user ? (
                <>
                  <p className="text-sm text-[#6b5f8a] px-4 mb-3">{user.email}</p>
                  <Button
                    variant="outline"
                    className="w-full border-[rgba(124,106,240,0.3)] text-[#18151f]"
                    onClick={() => {
                      handleSignOut();
                      setMobileMenuOpen(false);
                    }}
                  >
                    <LogOut className="mr-2 h-4 w-4" />
                    Sign Out
                  </Button>
                </>
              ) : (
                <Link
                  to="/create"
                  onClick={() => setMobileMenuOpen(false)}
                  className="block w-full text-center text-white font-medium py-3 rounded-full"
                  style={{ background: "#18151f" }}
                >
                  Start Creating
                </Link>
              )}
            </div>
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;
